import React, { useState, useEffect } from 'react';
import { useAsset } from '../hooks/useInvestQueries';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import AppLayout from '../components/layouts/AppLayout';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: asset, isLoading: loading, error } = useAsset(assetId);
  const [percentage, setPercentage] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (asset) {
      setPercentage(asset.min_percentage);
    }
  }, [asset]);

  if (error) {
    toast.error('Asset not found');
    navigate('/assets');
    return null;
  }

  const handleCommit = async () => {
    if (percentage < asset.min_percentage || percentage > asset.percent_remaining) {
      toast.error(`Please enter a percentage between ${asset.min_percentage}% and ${asset.percent_remaining}%`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/commitments', { asset_id: assetId, percentage });
      toast.success('Commitment submitted! Awaiting admin approval.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Commitment failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <AppLayout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div></div></AppLayout>;
  }

  const amount = (percentage / 100) * asset.total_value;

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="asset-detail-page">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/assets')} className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors mb-6" data-testid="back-to-assets-btn">
            <ArrowLeft className="w-5 h-5" />
            Back to Assets
          </button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {asset.images && asset.images.length > 0 && (
                <div className="w-full h-96 rounded-xl mb-6 overflow-hidden">
                <img src={asset.images[0]} alt={asset.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="glass-panel p-8">
              <h1 className="font-['Outfit'] text-4xl font-bold mb-4" data-testid="asset-name">{asset.name}</h1>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6" data-testid="asset-description">{asset.description}</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider block mb-1">Total Value</span>
                  <span className="font-['JetBrains_Mono'] text-2xl font-bold" data-testid="asset-total-value">${asset.total_value.toLocaleString()}</span>
                </div>
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider block mb-1">Committed</span>
                  <span className="font-['JetBrains_Mono'] text-2xl font-bold text-[#F59E0B]" data-testid="asset-percent-committed">{asset.percent_committed}%</span>
                </div>
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider block mb-1">Available</span>
                  <span className="font-['JetBrains_Mono'] text-2xl font-bold text-[#10B981]" data-testid="asset-percent-remaining">{asset.percent_remaining}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="glass-panel p-6 sticky top-8">
              <h3 className="font-['Outfit'] text-2xl font-bold mb-6">Investment Calculator</h3>
              <div className="mb-6">
                <label className="block text-sm text-[#94A3B8] mb-3">Your Investment Percentage</label>
                <div className="flex items-center gap-4 mb-4">
                  <input type="number" value={percentage} onChange={(e) => setPercentage(Number(e.target.value))} min={asset.min_percentage} max={asset.percent_remaining} step="0.1" className="w-24 px-4 py-2 bg-white/5 border border-white/10 rounded-lg font-['JetBrains_Mono'] text-2xl font-bold text-center focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" data-testid="percentage-input" />
                  <span className="text-2xl font-bold text-[#D4AF37]">%</span>
                </div>
                <Slider value={[percentage]} onValueChange={(val) => setPercentage(val[0])} min={asset.min_percentage} max={Math.min(asset.percent_remaining, 100)} step={0.1} className="mb-2" data-testid="percentage-slider" />
                <div className="flex justify-between text-xs text-[#64748B]">
                  <span>Min: {asset.min_percentage}%</span>
                  <span>Max: {asset.percent_remaining}%</span>
                </div>
              </div>
              <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg mb-6">
                <span className="text-[#94A3B8] text-sm uppercase tracking-wider block mb-1">Investment Amount</span>
                <span className="font-['JetBrains_Mono'] text-3xl font-bold text-[#D4AF37]" data-testid="calculated-amount">${amount.toLocaleString()}</span>
              </div>
              <button onClick={handleCommit} disabled={submitting || user?.role === 'admin' || percentage < asset.min_percentage || percentage > asset.percent_remaining} className="w-full py-3 bg-[#D4AF37] text-[#0A1128] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="commit-btn">
                {submitting ? 'Submitting...' : 'Commit to Investment'}
              </button>
              <p className="text-xs text-[#64748B] mt-4 text-center">This is a commitment only. Payment instructions will be sent after admin approval.</p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </AppLayout>
  );
}
